import React, { useState } from 'react';
import { Box, LinearProgress, List, Typography } from '@mui/material';
import { useCategoryContext } from '../contexts/CategoryContext';
import useCategories from '../hooks/data/useCategories';
import useBookmarksByCategory from '../hooks/data/useBookmarksByCategory';
import CategoryFolderItem from './CategoryFolderItem';
import BookmarkCard from './BookmarkCard';
import BookmarkCompactItem from './BookmarkCompactItem';
import BookmarkListView from './BookmarkListView';
import BookmarkPreviewDrawer from './BookmarkPreviewDrawer';
import { Bookmark } from '../types/bookmarks';
import { ViewMode } from '../hooks/useViewMode';

interface MainContentAreaProps {
  viewMode?: ViewMode;
}

const MainContentArea: React.FC<MainContentAreaProps> = ({
  viewMode = 'card',
}) => {
  const { selectedCategory, highlightedBookmarkId, setHighlightedBookmarkId } =
    useCategoryContext();
  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(
    null,
  );
  const [previewBookmark, setPreviewBookmark] = useState<Bookmark | null>(null);

  // Fetch child categories for selected category (or root categories when null)
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategories(selectedCategory?.category_id ?? null);

  // Fetch bookmarks for selected category (null when at root)
  const {
    data: bookmarks,
    loading: bookmarksLoading,
    error: bookmarksError,
  } = useBookmarksByCategory(selectedCategory?.category_id ?? undefined);

  const loading = categoriesLoading || bookmarksLoading;
  const error = categoriesError || bookmarksError;

  if (loading) {
    return (
      <Box sx={{ px: 2, pt: 1, flex: 1 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 2, pt: 2, flex: 1 }}>
        <Typography color="error">
          {error.message || 'An error occurred while loading data.'}
        </Typography>
      </Box>
    );
  }

  const hasCategories = categories.length > 0;
  const hasBookmarks = bookmarks && bookmarks.length > 0;

  // Empty state: no child categories, no bookmarks
  if (!hasCategories && !hasBookmarks) {
    return (
      <Box sx={{ px: 2, pt: 2, flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {selectedCategory ? 'This category is empty' : 'No categories found'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 2,
        flex: 1,
        bgcolor: viewMode === 'compact' ? 'grey.100' : undefined,
      }}
    >
      {/* Child categories displayed as folder items */}
      {hasCategories && (
        <List disablePadding>
          {categories.map((category) => (
            <CategoryFolderItem
              key={category.category_id}
              category={category}
            />
          ))}
        </List>
      )}

      {/* Bookmarks rendered by view mode */}
      {hasBookmarks &&
        viewMode === 'card' &&
        bookmarks!.map((bookmark) => (
          <BookmarkCard
            key={bookmark.bookmark_id}
            bookmark={bookmark}
            isEditing={editingBookmarkId === bookmark.bookmark_id}
            highlighted={highlightedBookmarkId === bookmark.bookmark_id}
            onHighlightComplete={() => setHighlightedBookmarkId(null)}
            onEditStart={() => setEditingBookmarkId(bookmark.bookmark_id)}
            onEditEnd={() => setEditingBookmarkId(null)}
            onClick={() => setPreviewBookmark(bookmark)}
          />
        ))}

      {hasBookmarks &&
        viewMode === 'compact' &&
        bookmarks!.map((bookmark) =>
          editingBookmarkId === bookmark.bookmark_id ? (
            <BookmarkCard
              key={bookmark.bookmark_id}
              bookmark={bookmark}
              isEditing={true}
              onEditStart={() => setEditingBookmarkId(bookmark.bookmark_id)}
              onEditEnd={() => setEditingBookmarkId(null)}
            />
          ) : (
            <BookmarkCompactItem
              key={bookmark.bookmark_id}
              bookmark={bookmark}
              highlighted={highlightedBookmarkId === bookmark.bookmark_id}
              onHighlightComplete={() => setHighlightedBookmarkId(null)}
              onClick={() => setPreviewBookmark(bookmark)}
              onEditStart={() => setEditingBookmarkId(bookmark.bookmark_id)}
            />
          ),
        )}

      {hasBookmarks &&
        viewMode === 'list' &&
        editingBookmarkId &&
        bookmarks!
          .filter((b) => b.bookmark_id === editingBookmarkId)
          .map((bookmark) => (
            <BookmarkCard
              key={bookmark.bookmark_id}
              bookmark={bookmark}
              isEditing={true}
              onEditStart={() => setEditingBookmarkId(bookmark.bookmark_id)}
              onEditEnd={() => setEditingBookmarkId(null)}
            />
          ))}

      {hasBookmarks && viewMode === 'list' && (
        <BookmarkListView
          bookmarks={bookmarks!}
          highlightedBookmarkId={highlightedBookmarkId}
          onHighlightComplete={() => setHighlightedBookmarkId(null)}
          onClick={(bookmark) => setPreviewBookmark(bookmark)}
          onEditStart={(id) => setEditingBookmarkId(id)}
        />
      )}

      <BookmarkPreviewDrawer
        bookmark={previewBookmark}
        open={!!previewBookmark}
        onClose={() => setPreviewBookmark(null)}
        onEdit={() => {
          if (previewBookmark) {
            setEditingBookmarkId(previewBookmark.bookmark_id);
            setPreviewBookmark(null);
          }
        }}
      />
    </Box>
  );
};

export default MainContentArea;
